// src/services/auth.ts

import api from './api';
import { User } from '../types';

export interface LoginSuccessResult {
  alreadyLoggedIn: false;
  message: string;
  user: User;
  token: string;
  capabilities: string[];
}

export interface LoginConflictResult {
  alreadyLoggedIn: true;
  message: string;
  user: null;
  token: null;
  capabilities: [];
}

export type LoginResult = LoginSuccessResult | LoginConflictResult;

export const authService = {
  login: async (email: string, password: string, force: boolean = false): Promise<LoginResult> => {
    try {
      const res = await api.post('/login', { email, password, force });

      if (!res.data) {
        throw new Error('Empty response from server');
      }

      const responseData = res.data;

      if (responseData.already_logged_in === true) {
        return {
          alreadyLoggedIn: true,
          message: responseData.message || 'Akun ini masih login di perangkat lain.',
          user: null,
          token: null,
          capabilities: [],
        };
      }

      const user = responseData.data?.user;
      const token = responseData.data?.token;
      const capabilities = responseData.data?.capabilities || [];

      if (!user || !token) {
        console.error('Login response missing user or token', { user, token });
        throw new Error('Invalid response format: missing user or token');
      }

      return {
        alreadyLoggedIn: false,
        message: responseData.message || 'Login berhasil.',
        user,
        token,
        capabilities,
      };
    } catch (err: any) {
      console.error('Login error:', err);
      if (err.response?.data) {
        console.error('Response data:', err.response.data);
      }
      throw err;
    }
  },

  logout: async () => {
    await api.post('/logout');
  },

  getUser: async () => {
    const res = await api.get('/user');
    const data = res.data.data || res.data;

    const capabilities = data.capabilities || res.data.capabilities || [];

    if (capabilities.length > 0) {
      localStorage.setItem('capabilities', JSON.stringify(capabilities));
    }

    const { capabilities: _, ...userData } = data;
    return userData as User;
  },

  forgotPassword: async (email: string) => {
    const res = await api.post('/forgot-password', { email });
    return res.data;
  },

  resetPassword: async (data: { token: string; email: string; password: string; password_confirmation: string }) => {
    const res = await api.post('/reset-password', data);
    return res.data;
  },

  updateProfile: async (data: {
    name?: string;
    phone?: string;
    avatar?: File;
  }): Promise<User> => {
    const formData = new FormData();
    if (data.name) formData.append('name', data.name);
    if (data.phone !== undefined) formData.append('phone', data.phone);
    if (data.avatar) formData.append('avatar', data.avatar);

    const res = await api.post('/user/profile', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    const updatedUser = res.data.data || res.data;
    localStorage.setItem('user', JSON.stringify(updatedUser));

    return updatedUser as User;
  },

  changePassword: async (data: { current_password: string; password: string; password_confirmation: string }) => {
    const res = await api.put('/user/password', data);
    return res.data;
  },

  getSettings: async () => {
    const res = await api.get('/user/settings');
    return res.data.data as {
      email_notifications: boolean;
      browser_notifications: boolean;
      language: string;
      timezone: string;
    };
  },

  updateSettings: async (data: {
    email_notifications?: boolean;
    browser_notifications?: boolean;
    language?: string;
    timezone?: string;
  }) => {
    const res = await api.put('/user/settings', data);
    return res.data;
  },
};
